import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { UuidService } from './uuid.service';
import { WorkerUploadService } from './worker-upload.service';
import { db, SyncQueueItem } from '../indexed-db/db';
import { ReportEntryDto } from '../interfaces/dto/report-entry.interface';
import { environment } from '../../../environments/environment';

export interface QueueStatus {
  pending: number;
  syncing: number;
  failed: number;
  completed: number;
  total: number;
}

export interface SyncResult {
  queueId: string;
  status: 'queued' | 'syncing' | 'completed' | 'failed';
}

@Injectable({
  providedIn: 'root',
})
export class BackgroundSyncService {
  private httpClient = inject(HttpClient);
  private uuidService = inject(UuidService);
  private msalService = inject(MsalService);
  private workerUpload = inject(WorkerUploadService);
  private queueStatus$ = new BehaviorSubject<QueueStatus>({
    pending: 0,
    syncing: 0,
    failed: 0,
    completed: 0,
    total: 0,
  });
  private isServiceWorkerAvailable = false;
  private maxConcurrentUploads = 3; // Configurable max parallel uploads
  private activeUploads = new Set<string>(); // Track active upload IDs

  // Progress tracking
  private itemProgress$ = new Map<string, BehaviorSubject<number>>();
  private progressUpdates$ = new Subject<{ itemId: string; progress: number; speed?: number; eta?: number }>();
  private itemStatusChanged$ = new Subject<{ itemId: string; status: string; type: string }>();
  constructor() {
    this.updateQueueStatus();
    this.checkServiceWorkerAvailability();
    this.setupFallbackSync();

    // Check for pending items on startup and trigger sync if needed
    this.checkPendingItemsOnStartup();

    // Expose service for debugging in console
    if (!environment.production) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Debug helper for development console access
      (window as any).backgroundSyncService = this;
      console.log('[BackgroundSyncService] Service exposed as window.backgroundSyncService for debugging');
      console.log('Available debug methods:');
      console.log('  - backgroundSyncService.triggerSync() - Manually trigger sync');
      console.log('  - backgroundSyncService.getQueueItems() - View queue items');
      console.log('  - backgroundSyncService.clearQueue() - Clear all queue items');
      console.log('  - backgroundSyncService.clearPendingItems() - Clear only pending items');
      console.log('  - backgroundSyncService.removeDuplicates() - Remove duplicate entries');
      console.log('  - backgroundSyncService.isServiceWorkerEnabled() - Check SW status');
      console.log('  - backgroundSyncService.forceProcessItem(itemId) - Force process specific item');
      console.log('  - backgroundSyncService.setMaxConcurrentUploads(n) - Set max parallel uploads (1-10)');
      console.log('  - backgroundSyncService.getMaxConcurrentUploads() - Get max concurrent uploads');
      console.log('  - backgroundSyncService.getActiveUploadCount() - Get current active uploads');
    }
  }

  private async checkPendingItemsOnStartup(): Promise<void> {
    // Wait a bit for everything to initialize
    setTimeout(async () => {
      const pendingCount = await db.syncQueue.where('status').equals('pending').count();
      if (pendingCount > 0) {
        // Check if auto-sync is enabled
        const syncEnabled = localStorage.getItem('syncEnabled') !== 'false';

        if (syncEnabled) {
          // Remove any duplicates first
          await this.removeDuplicates();
          // Then trigger sync
          if (navigator.onLine) {
            this.triggerSync();
          }
        }
      }
    }, 2000);
  }

  private checkServiceWorkerAvailability(): void {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      this.isServiceWorkerAvailable = true;
      this.registerServiceWorker();
    } else {
      this.isServiceWorkerAvailable = false;
    }
  }

  private setupFallbackSync(): void {
    // For dev mode or when service worker isn't available
    // Check for pending items every 10 seconds
    if (!this.isServiceWorkerAvailable) {
      setInterval(() => {
        // Check if auto-sync is enabled
        const syncEnabled = localStorage.getItem('syncEnabled') !== 'false';
        if (navigator.onLine && syncEnabled) {
          this.processSyncQueue().catch((err) => console.error('[BackgroundSyncService] Fallback sync error:', err));
        }
      }, 10000); // Check every 10 seconds
    }

    // Also check for stuck syncing items periodically
    setInterval(() => {
      this.checkForStuckItems();
    }, 30000); // Check every 30 seconds

    // Also sync when coming online (but only if auto-sync is enabled)
    window.addEventListener('online', () => {
      const syncEnabled = localStorage.getItem('syncEnabled') !== 'false';
      if (syncEnabled) {
        this.processSyncQueue();
      }
    });
  }

  // Queue Management
  async queueItem(item: Partial<SyncQueueItem>): Promise<string> {
    const queueItem: SyncQueueItem = {
      id: this.uuidService.generate(),
      type: item.type || 'generic',
      endpoint: item.endpoint || '',
      method: item.method || 'POST',
      payload: item.payload,
      metadata: item.metadata || {},
      priority: item.priority || 5,
      status: 'pending',
      retryCount: 0,
      maxRetries: item.maxRetries || 3,
      createdAt: new Date(),
      ...item,
    };

    await db.syncQueue.add(queueItem);
    await this.updateQueueStatus();

    // Check if auto-sync is enabled
    const syncEnabled = localStorage.getItem('syncEnabled') !== 'false';

    // Handle sync based on environment and auto-sync setting
    if (navigator.onLine && syncEnabled) {
      if (this.isServiceWorkerAvailable) {
        this.requestSync(queueItem.priority >= 8 ? 'high-priority' : 'normal');
      } else {
        // No service worker (dev mode) - process immediately
        setTimeout(() => {
          this.processSyncQueue().catch((err) => console.error('[BackgroundSyncService] Immediate sync failed:', err));
        }, 100); // Small delay to ensure UI updates first
      }
    }

    return queueItem.id!;
  }

  getQueueStatus(): Observable<QueueStatus> {
    return this.queueStatus$.asObservable();
  }

  // Get progress updates for a specific item
  getItemProgress(itemId: string): Observable<number> {
    if (!this.itemProgress$.has(itemId)) {
      this.itemProgress$.set(itemId, new BehaviorSubject<number>(0));
    }
    return this.itemProgress$.get(itemId)!.asObservable();
  }

  // Get all progress updates
  getProgressUpdates(): Observable<{ itemId: string; progress: number; speed?: number; eta?: number }> {
    return this.progressUpdates$.asObservable();
  }

  getItemStatusChanges(): Observable<{ itemId: string; status: string; type: string }> {
    return this.itemStatusChanged$.asObservable();
  }

  // Type-specific helpers
  async queueReportEntry(reportId: string, entry: ReportEntryDto): Promise<string> {
    // Check for duplicate entries before queuing
    const existingItems = await db.syncQueue
      .where('type')
      .equals('report-entry')
      .and(
        (item) =>
          item.status !== 'completed' &&
          item.metadata?.['reportId'] === reportId &&
          item.metadata?.['version'] === entry.version
      )
      .toArray();

    if (existingItems.length > 0) {
      // Return the existing item's ID instead of creating a duplicate
      return existingItems[0].id!;
    }

    return this.queueItem({
      type: 'report-entry',
      endpoint: `${environment.apiConfig.apiBaseUrl}${environment.apiConfig.apiUri}/reports/${reportId}/entry`,
      method: 'POST',
      payload: entry,
      metadata: {
        reportId,
        inspectionId: entry.id,
        version: entry.version,
      },
      priority: 8, // High priority for report entries
      maxRetries: 3,
    });
  }

  async queueImage(imageData: string, filename: string, mimeType: string, metadata: unknown): Promise<string> {
    // Extract base64 data if it includes data URI prefix
    const base64Data = imageData.includes(',') ? imageData.split(',')[1] : imageData;
    const size = Math.round(base64Data.length * 0.75);

    return this.queueItem({
      type: 'image',
      endpoint: `${environment.apiConfig.apiBaseUrl}${environment.apiConfig.apiUri}/images`,
      method: 'POST',
      payload: {
        imageData: base64Data,
        filename,
        mimeType,
        id: (metadata as Record<string, unknown>)?.['objectStoreRef'] as string | undefined, // Send objectStoreRef as 'id' for backend
      },
      metadata: {
        size,
        filename,
        mimeType,
        ...(metadata || {}),
      },
      priority: 5, // Normal priority for images
      maxRetries: 5, // More retries for larger files
    });
  }

  // Sync Control
  async processSyncQueue(): Promise<void> {
    const pendingItems = await db.syncQueue.where('status').equals('pending').toArray();

    // Sort by priority first (high to low), then by createdAt (oldest first - FIFO)
    const sortedItems = pendingItems.sort((a, b) => {
      // Higher priority first
      const priorityDiff = b.priority - a.priority;
      if (priorityDiff !== 0) return priorityDiff;

      // Then oldest first (FIFO)
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB; // Oldest first for processing
    });

    // Process items in parallel with concurrency limit
    const uploadPromises: Promise<void>[] = [];

    for (const item of sortedItems) {
      // Wait if we've reached max concurrent uploads
      while (this.activeUploads.size >= this.maxConcurrentUploads) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Start upload without awaiting (parallel processing)
      const uploadPromise = this.syncItem(item).finally(() => {
        // Remove from active uploads when done
        this.activeUploads.delete(item.id!);
        this.updateQueueStatus();
      });

      // Track active upload
      this.activeUploads.add(item.id!);
      uploadPromises.push(uploadPromise);
    }

    // Wait for all uploads to complete
    await Promise.allSettled(uploadPromises);
    await this.updateQueueStatus();
  }

  // Manual trigger for debugging
  async triggerSync(): Promise<void> {
    await this.processSyncQueue();
  }

  // Debug helpers
  async getQueueItems(): Promise<SyncQueueItem[]> {
    const items = await db.syncQueue.toArray();
    // Sort by createdAt date, newest first
    return items.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Newest first
    });
  }

  async getQueueItem(itemId: string): Promise<SyncQueueItem | undefined> {
    return db.syncQueue.get(itemId);
  }

  async clearQueue(): Promise<void> {
    await db.syncQueue.clear();
    await this.updateQueueStatus();
  }

  async clearPendingItems(): Promise<void> {
    await db.syncQueue.where('status').equals('pending').delete();
    await this.updateQueueStatus();
  }

  async removeDuplicates(): Promise<void> {
    const allItems = await db.syncQueue.toArray();
    const seen = new Map<string, string>();
    const toDelete: string[] = [];

    for (const item of allItems) {
      if (item.type === 'report-entry' && item.status !== 'completed') {
        const key = `${item.metadata?.['reportId']}-${item.metadata?.['version']}`;
        if (seen.has(key)) {
          // Keep the first one, delete this duplicate
          toDelete.push(item.id!);
        } else {
          seen.set(key, item.id!);
        }
      }
    }

    if (toDelete.length > 0) {
      for (const id of toDelete) {
        await db.syncQueue.delete(id);
      }
      await this.updateQueueStatus();
    }
  }

  async forceProcessItem(itemId: string): Promise<void> {
    const item = await db.syncQueue.get(itemId);
    if (item) {
      await this.syncItem(item);
      await this.updateQueueStatus();
    } else {
      console.error(`[BackgroundSyncService] Item ${itemId} not found`);
    }
  }

  isServiceWorkerEnabled(): boolean {
    return this.isServiceWorkerAvailable;
  }

  // Configure max concurrent uploads
  setMaxConcurrentUploads(max: number): void {
    this.maxConcurrentUploads = Math.max(1, Math.min(10, max)); // Between 1 and 10
  }

  getMaxConcurrentUploads(): number {
    return this.maxConcurrentUploads;
  }

  // Get current active upload count
  getActiveUploadCount(): number {
    return this.activeUploads.size;
  }

  // Check if parallel uploads are in progress
  hasActiveUploads(): boolean {
    return this.activeUploads.size > 0;
  }

  // Cancel an upload in progress
  cancelUpload(itemId: string): void {
    // Cancel Web Worker if it exists
    this.workerUpload.cancelUpload(itemId);

    // Update item status in database
    db.syncQueue.update(itemId, {
      status: 'cancelled',
      error: 'Cancelled by user',
    } as Partial<SyncQueueItem>);

    // Clean up progress tracking
    if (this.itemProgress$.has(itemId)) {
      this.itemProgress$.get(itemId)?.complete();
      this.itemProgress$.delete(itemId);
    }
  }

  // Check for items that have been syncing for too long
  private async checkForStuckItems(): Promise<void> {
    const STUCK_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

    try {
      const syncingItems = await db.syncQueue.where('status').equals('syncing').toArray();

      const now = new Date().getTime();

      for (const item of syncingItems) {
        if (item.uploadStartTime) {
          const syncingDuration = now - new Date(item.uploadStartTime).getTime();

          // If item has been syncing for more than threshold and has no progress
          if (syncingDuration > STUCK_THRESHOLD_MS && (!item.uploadProgress || item.uploadProgress === 0)) {
            console.warn(`[BackgroundSyncService] Item ${item.id} appears stuck, marking as failed`);

            await db.syncQueue.update(item.id!, {
              status: 'failed',
              error: 'Upload timeout - stuck in syncing state',
              uploadProgress: 0,
            } as Partial<SyncQueueItem>);

            // Clean up progress tracking
            if (this.itemProgress$.has(item.id!)) {
              this.itemProgress$.get(item.id!)?.complete();
              this.itemProgress$.delete(item.id!);
            }
          }
        }
      }
    } catch (error) {
      console.error('[BackgroundSyncService] Error checking for stuck items:', error);
    }
  }

  async retryFailedItems(type?: string): Promise<void> {
    const query = db.syncQueue.where('status').anyOf(['failed', 'cancelled']);

    const failedItems = await query.toArray();
    const itemsToRetry = type ? failedItems.filter((item) => item.type === type) : failedItems;

    for (const item of itemsToRetry) {
      if (item.retryCount < item.maxRetries) {
        await db.syncQueue.update(item.id!, {
          status: 'pending',
          retryCount: item.retryCount + 1,
          error: undefined,
        } as Partial<SyncQueueItem>);
      }
    }

    await this.updateQueueStatus();
    await this.processSyncQueue();
  }

  async clearCompletedItems(): Promise<void> {
    await db.syncQueue.where('status').equals('completed').delete();
    await this.updateQueueStatus();
  }

  // Private methods
  private async syncItem(item: SyncQueueItem): Promise<void> {
    // Calculate total size for progress tracking
    let totalSize = 0;
    if (item.type === 'image' && item.metadata?.size) {
      totalSize = item.metadata.size;
    } else if (item.payload) {
      // Estimate size from JSON payload
      totalSize = JSON.stringify(item.payload).length;
    }

    // Update status to syncing with initial progress
    const uploadStartTime = new Date();
    await db.syncQueue.update(item.id!, {
      status: 'syncing',
      lastAttemptAt: uploadStartTime,
      uploadStartTime,
      uploadProgress: 0,
      uploadedBytes: 0,
      totalBytes: totalSize,
    } as Partial<SyncQueueItem>);

    // Emit status change event
    this.itemStatusChanged$.next({
      itemId: item.id!,
      status: 'syncing',
      type: item.type,
    });

    // Update queue status immediately after changing to syncing
    await this.updateQueueStatus();

    // Initialize progress tracking
    if (!this.itemProgress$.has(item.id!)) {
      this.itemProgress$.set(item.id!, new BehaviorSubject<number>(0));
    }

    try {
      // Prepare request based on payload type
      let body: unknown = item.payload;
      let httpHeaders = new HttpHeaders();

      // For images, check if we should use streaming
      const useStreaming = item.type === 'image' && navigator.onLine && item.metadata?.['useStreaming'];

      // Modify endpoint for streaming uploads
      let endpoint = item.endpoint;

      const payload = item.payload as { imageData?: string; mimeType?: string; filename?: string; id?: string };
      if (useStreaming && payload.imageData) {
        // Convert base64 back to blob for streaming
        const base64Data = payload.imageData;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: payload.mimeType || 'image/jpeg' });

        // Create FormData for streaming
        const formData = new FormData();
        formData.append('file', blob, payload.filename || 'image.jpg');
        if (payload.id) {
          formData.append('id', payload.id);
        }

        body = formData;
        // Don't set Content-Type for FormData, let browser set it with boundary

        // Use streaming endpoint
        endpoint = endpoint.replace('/images', '/images/stream');
      } else {
        // For JSON payloads, set the content type
        httpHeaders = httpHeaders.set('Content-Type', 'application/json');
      }

      // Use Web Worker for uploads with real progress tracking
      const useWorker =
        (item.type === 'image' && useStreaming && body instanceof FormData) ||
        (item.type === 'report-entry' && navigator.onLine);

      if (useWorker) {
        try {
          // Use appropriate worker method based on data type
          if (body instanceof FormData) {
            // Image upload with FormData
            await this.workerUpload.uploadWithWorker(item.id!, endpoint, body, (progress) => {
              // Update progress in IndexedDB
              db.syncQueue
                .update(item.id!, {
                  uploadProgress: progress.progress,
                  uploadedBytes: progress.loaded,
                  totalBytes: progress.total,
                  uploadSpeed: progress.speed,
                  uploadEta: progress.eta,
                } as Partial<SyncQueueItem>)
                .catch((err) => console.error('[BackgroundSyncService] Failed to update progress in DB:', err));

              // Emit progress updates
              this.itemProgress$.get(item.id!)?.next(progress.progress);
              this.progressUpdates$.next({
                itemId: item.id!,
                progress: progress.progress,
                speed: progress.speed,
                eta: progress.eta,
              });
            });

            // Mark as completed with 100% progress
            await db.syncQueue.update(item.id!, {
              status: 'completed',
              completedAt: new Date(),
              uploadProgress: 100,
              uploadedBytes: totalSize,
              totalBytes: totalSize,
            } as Partial<SyncQueueItem>);

            // Emit status change event
            this.itemStatusChanged$.next({
              itemId: item.id!,
              status: 'completed',
              type: item.type,
            });

            // Update progress to 100%
            this.itemProgress$.get(item.id!)?.next(100);
            this.itemProgress$.get(item.id!)?.complete();
            this.itemProgress$.delete(item.id!);

            // Notify success
            this.notifySyncSuccess(item);
            return; // Early return for successful Web Worker upload
          } else {
            // Report entry upload with JSON
            await this.workerUpload.uploadJson(
              item.id!,
              endpoint,
              body,
              item.method as 'POST' | 'PUT' | 'PATCH',
              (progress) => {
                // Update progress in IndexedDB
                db.syncQueue
                  .update(item.id!, {
                    uploadProgress: progress.progress,
                    uploadedBytes: progress.loaded,
                    totalBytes: progress.total,
                    uploadSpeed: progress.speed,
                    uploadEta: progress.eta,
                  } as Partial<SyncQueueItem>)
                  .catch((err) => console.error('[BackgroundSyncService] Failed to update progress in DB:', err));

                // Emit progress updates
                this.itemProgress$.get(item.id!)?.next(progress.progress);
                this.progressUpdates$.next({
                  itemId: item.id!,
                  progress: progress.progress,
                  speed: progress.speed,
                  eta: progress.eta,
                });
              }
            );

            // Mark as completed with 100% progress
            await db.syncQueue.update(item.id!, {
              status: 'completed',
              completedAt: new Date(),
              uploadProgress: 100,
              uploadedBytes: totalSize,
              totalBytes: totalSize,
            } as Partial<SyncQueueItem>);

            // Emit status change event
            this.itemStatusChanged$.next({
              itemId: item.id!,
              status: 'completed',
              type: item.type,
            });

            // Update progress to 100%
            this.itemProgress$.get(item.id!)?.next(100);
            this.itemProgress$.get(item.id!)?.complete();
            this.itemProgress$.delete(item.id!);

            // Notify success
            this.notifySyncSuccess(item);
            return; // Early return for successful Web Worker upload
          }
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error(`[BackgroundSyncService] Web Worker upload failed: ${errorMessage}`);
          throw error; // Let it fall through to error handling
        }
      }

      // For non-image requests, use HttpClient as before
      const response = await new Promise<HttpResponse<unknown>>((resolve, reject) => {
        // Create the request with proper headers
        const req = new HttpRequest(item.method as 'POST' | 'PUT' | 'PATCH', endpoint, body, {
          headers: httpHeaders,
          reportProgress: true,
          responseType: 'json',
        });

        const request$ = this.httpClient.request(req);

        request$.subscribe({
          next: (event: HttpEvent<unknown>) => {
            if (event.type === HttpEventType.UploadProgress) {
              // Update progress
              if (event.total) {
                const progress = Math.round((100 * event.loaded) / event.total);
                const elapsedSeconds = (Date.now() - uploadStartTime.getTime()) / 1000;
                const speed = elapsedSeconds > 0 ? event.loaded / elapsedSeconds : 0;
                const remaining = event.total - event.loaded;
                const eta = speed > 0 ? Math.round(remaining / speed) : undefined;

                // Update progress in IndexedDB
                db.syncQueue.update(item.id!, {
                  uploadProgress: progress,
                  uploadedBytes: event.loaded,
                  totalBytes: event.total,
                  uploadSpeed: speed,
                  uploadEta: eta,
                } as Partial<SyncQueueItem>);

                // Emit progress updates
                this.itemProgress$.get(item.id!)?.next(progress);
                this.progressUpdates$.next({
                  itemId: item.id!,
                  progress,
                  speed,
                  eta,
                });
              }
            } else if (event.type === HttpEventType.Response) {
              // Update progress to 100% on completion
              db.syncQueue.update(item.id!, {
                uploadProgress: 100,
                uploadedBytes: totalSize,
                totalBytes: totalSize,
              } as Partial<SyncQueueItem>);

              // Emit 100% progress
              this.itemProgress$.get(item.id!)?.next(100);
              this.progressUpdates$.next({
                itemId: item.id!,
                progress: 100,
                speed: 0,
                eta: 0,
              });

              resolve(event);
            }
          },
          error: (err) => {
            console.error(`[BackgroundSyncService] Request failed:`, err);
            reject(err);
          },
        });
      });

      if (response && response.ok && response.status >= 200 && response.status < 300) {
        // Success - mark as completed with 100% progress
        await db.syncQueue.update(item.id!, {
          status: 'completed',
          completedAt: new Date(),
          uploadProgress: 100,
        } as Partial<SyncQueueItem>);

        // Emit status change event
        this.itemStatusChanged$.next({
          itemId: item.id!,
          status: 'completed',
          type: item.type,
        });

        // Update progress to 100%
        this.itemProgress$.get(item.id!)?.next(100);
        this.itemProgress$.get(item.id!)?.complete();
        this.itemProgress$.delete(item.id!);

        // Notify success via postMessage if in service worker context
        this.notifySyncSuccess(item);
      } else {
        throw new Error(`HTTP ${response?.status}: ${response?.statusText}`);
      }
    } catch (error: unknown) {
      // Failed - update retry count
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const retriesExhausted = item.retryCount >= item.maxRetries;

      if (retriesExhausted) {
        await db.syncQueue.update(item.id!, {
          status: 'failed',
          error: errorMessage,
          uploadProgress: 0,
        } as Partial<SyncQueueItem>);
        // Notify subscribers that this item has terminally failed. Without this, the
        // component's subscription only ever sees the initial 'syncing' event and stays
        // stuck on "uploading" forever — the symptom users see after an offline queue
        // drains into a 5xx from the gateway on reconnect.
        this.itemStatusChanged$.next({
          itemId: item.id!,
          status: 'failed',
          type: item.type,
        });
      } else {
        await db.syncQueue.update(item.id!, {
          status: 'pending',
          retryCount: item.retryCount + 1,
          error: errorMessage,
          uploadProgress: 0,
        } as Partial<SyncQueueItem>);
      }

      // Clean up progress tracking
      this.itemProgress$.get(item.id!)?.complete();
      this.itemProgress$.delete(item.id!);

      this.notifySyncFailure(item, errorMessage);
    }
  }

  private async updateQueueStatus(): Promise<void> {
    const [pending, syncing, failed, completed, cancelled] = await Promise.all([
      db.syncQueue.where('status').equals('pending').count(),
      db.syncQueue.where('status').equals('syncing').count(),
      db.syncQueue.where('status').equals('failed').count(),
      db.syncQueue.where('status').equals('completed').count(),
      db.syncQueue.where('status').equals('cancelled').count(),
    ]);

    const total = pending + syncing + failed + completed + cancelled;

    this.queueStatus$.next({
      pending,
      syncing,
      failed: failed + cancelled, // Include cancelled in failed count for UI
      completed,
      total,
    });
  }

  private requestSync(tag: string): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Background Sync API lacks TypeScript definitions
    if ('serviceWorker' in navigator && 'sync' in (navigator as any)) {
      navigator.serviceWorker.ready
        .then((registration) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Background Sync API lacks TypeScript definitions
          (registration as any).sync
            .register(tag)
            .then(() => {})
            .catch((err: unknown) => {
              console.error('[BackgroundSyncService] Sync registration failed:', err);
              // Fallback to immediate processing if sync registration fails
              setTimeout(() => this.processSyncQueue(), 1000);
            });
        })
        .catch((err) => {
          console.error('[BackgroundSyncService] Service worker not ready:', err);
          // Fallback to immediate processing
          setTimeout(() => this.processSyncQueue(), 1000);
        });
    } else {
      setTimeout(() => this.processSyncQueue(), 1000);
    }
  }

  private registerServiceWorker(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const data = event.data as
          | { type?: string; itemId?: string; itemType?: string }
          | undefined;
        if (!data?.type) return;

        if (data.type === 'SYNC_SUCCESS' && data.itemId) {
          this.itemStatusChanged$.next({
            itemId: data.itemId,
            status: 'completed',
            type: data.itemType ?? 'generic',
          });
          this.updateQueueStatus();
        } else if (data.type === 'SYNC_FAILED' && data.itemId) {
          this.itemStatusChanged$.next({
            itemId: data.itemId,
            status: 'failed',
            type: data.itemType ?? 'generic',
          });
          this.updateQueueStatus();
        } else if (data.type === 'SYNC_COMPLETE') {
          this.updateQueueStatus();
        }
      });
    }
  }

  private async notifySyncSuccess(item: SyncQueueItem): Promise<void> {
    // Update queue status after successful sync
    await this.updateQueueStatus();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.controller?.postMessage({
        type: 'SYNC_SUCCESS',
        itemId: item.id,
        itemType: item.type,
      });
    }
  }

  private async notifySyncFailure(item: SyncQueueItem, error: string): Promise<void> {
    // Update queue status after failed sync
    await this.updateQueueStatus();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.controller?.postMessage({
        type: 'SYNC_FAILURE',
        itemId: item.id,
        itemType: item.type,
        error,
      });
    }
  }
}
